import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import {
  Button,
  Card,
  Col,
  Container,
  Form,
  InputGroup,
  Row,
  Table,
} from "react-bootstrap";
import debounce from "lodash/debounce";
import { useReactToPrint } from "react-to-print";
import { InspectionsClient, DealersClient } from "../web-api-client.ts";
import QR from "./QR.js";
import { QRCodeCanvas } from "qrcode.react";
import UserContext from "../providers/UserContext.js";


// ─────────────────────────────────────────────────────────────────────────────
// Brand
// ─────────────────────────────────────────────────────────────────────────────
const BRAND = {
  green: "#2E7D32",
  greenDark: "#1B5E20",
  greenLight: "#4CAF50",
  red: "#C62828",
  dark: "#1C1C1C",
  white: "#FFFFFF",
  gray: "#555555",
  lightGray: "#E8E8E4",
};

const INSPECTION_FEATURES = [
  "50+ Point Inspection",
  "Undercarriage Photos",
  "Cosmetic Grading",
  "Transparency Score",
];

const fmt = {
  price: (n) =>
    n != null
      ? "$" + Number(n).toLocaleString("en-US", { maximumFractionDigits: 0 })
      : "—",
  miles: (n) =>
    n != null ? Number(n).toLocaleString("en-US") + " mi" : "",
};

// ─────────────────────────────────────────────────────────────────────────────
// CheckIcon
// ─────────────────────────────────────────────────────────────────────────────
const CheckIcon = ({ size = 28 }) => (
  <svg width={size} height={size} viewBox="0 0 28 28" fill="none">
    <circle cx="14" cy="14" r="14" fill={BRAND.greenLight} />
    <path
      d="M8 14.5l4.5 4.5 8-9"
      stroke={BRAND.white}
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);


/**
 * PrintSafeQR
 * - On SCREEN: shows fancy <QR /> preview
 * - On PRINT: shows the print-perfect layout (scanme image + QR + logo)
 */
const PrintSafeQR = ({ url, qrInches = 2.75 }) => {
  const QR_SIZE = 400;
  const MARGIN = 40;
  const EXTRA_RIGHT = 380;

  const ARROW_COLOR = "#C65A43";
  const ARROW_WIDTH = 28;
  const ARROW_HEAD = 44;

  const qrCanvasRef = useRef(null);
  const [pngSrc, setPngSrc] = useState("");

  useEffect(() => {
    let raf = 0;

    const compose = () => {
      const qr = qrCanvasRef.current;
      if (!qr) return;

      const W = MARGIN + QR_SIZE + EXTRA_RIGHT + MARGIN;
      const H = MARGIN + QR_SIZE + MARGIN;

      const comp = document.createElement("canvas");
      comp.width = W;
      comp.height = H;
      const ctx = comp.getContext("2d");

      ctx.fillStyle = "#FFFFFF";
      ctx.fillRect(0, 0, W, H);

      const qx = MARGIN;
      const qy = MARGIN;
      ctx.drawImage(qr, qx, qy);

      const endX = qx + QR_SIZE + 18;
      const endY = qy + QR_SIZE * 0.4;

      const startX = qx + QR_SIZE + EXTRA_RIGHT - 10;
      const startY = qy + QR_SIZE * 0.22;

      const c1x = startX + 20;
      const c1y = startY - 160;
      const c2x = endX + 160;
      const c2y = endY - 60;

      ctx.lineWidth = ARROW_WIDTH;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.strokeStyle = ARROW_COLOR;

      ctx.beginPath();
      ctx.moveTo(startX, startY);
      ctx.bezierCurveTo(c1x, c1y, c2x, c2y, endX, endY);
      ctx.stroke();

      const vx = endX - c2x;
      const vy = endY - c2y;
      const vlen = Math.hypot(vx, vy) || 1;
      const ux = vx / vlen;
      const uy = vy / vlen;
      const baseX = endX - ux * (ARROW_HEAD + 8);
      const baseY = endY - uy * (ARROW_HEAD + 8);
      const px = -uy;
      const py = ux;

      const leftX = baseX + px * (ARROW_HEAD * 0.65);
      const leftY = baseY + py * (ARROW_HEAD * 0.65);
      const rightX = baseX - px * (ARROW_HEAD * 0.65);
      const rightY = baseY - py * (ARROW_HEAD * 0.65);

      ctx.fillStyle = ARROW_COLOR;
      ctx.beginPath();
      ctx.moveTo(endX, endY);
      ctx.lineTo(leftX, leftY);
      ctx.lineTo(rightX, rightY);
      ctx.closePath();
      ctx.fill();

      try {
        setPngSrc(comp.toDataURL("image/png"));
      } catch {
        setPngSrc("");
      }
    };

    raf = requestAnimationFrame(compose);
    return () => cancelAnimationFrame(raf);
  }, [url]);

  const COMPOSITE_W = MARGIN + QR_SIZE + EXTRA_RIGHT + MARGIN;
  const qrToCompositeRatio = QR_SIZE / COMPOSITE_W;
  const imgInches = qrInches / qrToCompositeRatio;

  return (
    <>
      <div className="print-only" style={{ textAlign: "center" }}>
        <img
          src="scanme.png"
          alt="scan me with arrow"
          style={{
            width: `7in`,
            maxWidth: "100%",
            height: "auto",
            display: "block",
            margin: "0 auto",
          }}
        />

        <div style={{ marginTop: "-150px" }}>
          <QRCodeCanvas ref={qrCanvasRef} value={url} size={QR_SIZE} />
        </div>

        <img
          src="logo-dark.png"
          alt="qcar logo"
          style={{
            width: `4in`,
            maxWidth: "100%",
            height: "auto",
            display: "block",
            margin: "50px auto",
          }}
        />
      </div>

      <div className="screen-only">
        <QR url={url} />
      </div>

      <div
        style={{ position: "absolute", left: -99999 }}
        className="screen-only"
        aria-hidden="true"
      >
        {pngSrc ? (
          <img
            src={pngSrc}
            alt=""
            style={{ width: `${imgInches}in`, height: "auto" }}
          />
        ) : null}
      </div>
    </>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// WindowStickerPage — one full letter-page sticker
//
// Uses QRCodeCanvas directly (NOT PrintSafeQR) so that:
//   • The screen preview and the browser print dialog always look identical
//   • The scanme.png / logo-dark.png block from PrintSafeQR doesn't inflate
//     the page height past 11in
// ─────────────────────────────────────────────────────────────────────────────
export const WindowStickerPage = ({ item, dealerProfile }) => {
  const {
    id,
    vehicleYear = "",
    vehicleMake = "",
    vehicleModel = "",
    vehicleTrim = "",
    vehicleMileage,
    vehiclePrice,
  } = item;

  const {
    name = "Your Dealership",
    logoUrl = null,
    phone = "",
    website = "",
    address = "",
    slogan = "",
  } = dealerProfile || {};

  // Same URL as the QR-only sheet — one unique code per car
  const qrUrl = `${window.location.origin}/dvi/scan/${id}`;

  // 300 px renders crisply at ~3.1in on a 96-dpi screen and sharp in print
  const QR_PX = 400;

  return (
    <div
      className="window-sticker-page"
      style={{
        width: "8.5in",
        height: "11in",           // hard cap — forces single page
        background: BRAND.white,
        display: "flex",
        flexDirection: "column",
        fontFamily: "'Segoe UI', Arial, Helvetica, sans-serif",
        color: BRAND.dark,
        overflow: "hidden",       // clip anything that would spill
        boxSizing: "border-box",
      }}
    >
      {/* ── Zone 1: Dark header ── */}
      <div style={{
        background: "#696969",
        background: "radial-gradient(circle,rgba(105, 105, 105, 1) 0%, rgba(36, 41, 39, 1) 94%, rgba(41, 48, 45, 1) 60%, rgba(40, 40, 42, 1) 100%)", color: BRAND.white, textAlign: "center",
        padding: "12px 24px", fontSize: "30pt", letterSpacing: "0.04em",
        flexShrink: 0,
      }}>
        THIS CAR HAS <strong style={{ fontWeight: 700 }}>NOTHING TO HIDE</strong>
      </div>

      {/* ── Zone 2: Scan CTA ── */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "center",
        gap: "10px", padding: "8px 24px",
        borderBottom: `3px solid ${BRAND.greenLight}`, flexShrink: 0,
      }}>
        <CheckIcon size={34} />
        <span style={{ fontSize: "35pt", fontWeight: 700, color: BRAND.green }}>SCAN</span>
        <span style={{ fontSize: "30pt" }}>BEFORE <strong style={{ fontWeight: 700 }}>YOU BUY</strong></span>
      </div>

      {/* ── Zone 3: Vehicle name + trim / mileage ── */}
      <div style={{ padding: "10px 32px 4px", flexShrink: 0 }}>
        <div style={{ marginTop: "20px", fontSize: "40pt", fontWeight: 700, lineHeight: 1.15 }}>
          {[vehicleYear, vehicleMake, vehicleModel].filter(Boolean).join(" ")}

        </div>
        {(vehicleTrim || vehicleMileage) && (
          <div style={{ fontSize: "20pt", color: BRAND.gray, marginTop: "3px", fontWeight: "bold" }}>
            {vehicleTrim}
            {vehicleTrim && vehicleMileage ? " · " : ""}
            {vehicleMileage ? fmt.miles(vehicleMileage) : ""}
          </div>
        )}
      </div>

      {/* ── Zone 4: QR code + single large red arrow ── */}
      {/*
        QRCodeCanvas renders identically on screen AND in print — no hidden
        print-only / screen-only divs, so the preview always matches the
        print dialog.  The red arrow is a plain SVG positioned to the right
        of the QR, curving down-left to point at it.
      */}
      <div style={{
        flex: "1 1 auto", display: "flex", alignItems: "center",
        justifyContent: "center", position: "relative",
        padding: "8px 32px", minHeight: 0,
      }}>
        {/* Red arrow — right side, curves down toward QR */}
        {/* <svg
          viewBox="0 0 180 260"
          style={{
            position: "absolute",
            right: "4%",
            top: "50%",
            transform: "translateY(-58%)",
            width: "1.55in",
            height: "auto",
            pointerEvents: "none",
            overflow: "visible",
          }}
        >
          <path
            d="M 150 15 C 175 15, 178 140, 45 200"
            stroke={BRAND.red}
            strokeWidth="20"
            strokeLinecap="round"
            fill="none"
          />
          <polygon points="12,182 58,222 62,168" fill={BRAND.red} />
        </svg> */}
        {/* Red arrow */}
        <img src="redArrow.png" style={{
          position: "absolute",
          right: "10%",
          top: "30%",
          transform: "translateY(-58%)",
          width: "2in",
          height: "auto",
          pointerEvents: "none",
          overflow: "visible"
        }} />

        {/* Bare QR canvas — same value as the QR-only sheet for this car */}
        <QRCodeCanvas value={qrUrl} size={QR_PX} />
      </div>

      {/* ── Zone 5: Price (large) below the QR ── */}
      <div style={{
        textAlign: "center", padding: "0px 20px 2px", marginTop: "-10px",
        position: "relative", flexShrink: 0,
      }}>
        <div style={{ fontSize: "80pt", fontWeight: 700, lineHeight: 1, letterSpacing: "-0.02em" }}>
          {fmt.price(vehiclePrice)}
        </div>
        <div style={{ fontSize: "8.5pt", color: BRAND.gray, marginTop: "20px" }}>
          *Tax, title and fees not included
        </div>

        {/* Q-caR logo — bottom-right of price zone */}
        <div style={{ position: "absolute", right: "28px", bottom: "4px" }}>
          <img
            src="logo-dark.png"
            alt="Q-caR"
            style={{ height: "50px", width: "auto", objectFit: "contain" }}
          />
        </div>
      </div>

      {/* ── Zone 6: Dealer info  |  Feature checklist ── */}
      <div style={{
        display: "grid", gridTemplateColumns: "3fr 2fr",
        borderTop: `1px solid ${BRAND.lightGray}`, marginTop: "6px", flexShrink: 0,
      }}>
        {/* Dealer logo + name  (from /api/Dealers → logoUri + name) */}
        <div style={{
          padding: "10px 18px", display: "flex", alignItems: "center",
          gap: "10px", borderRight: `1px solid ${BRAND.lightGray}`,
        }}>
          {logoUrl ? (
            <img src={logoUrl} alt={name}
              style={{ background: BRAND.gray, height: "150px", width: "auto", maxWidth: "200px", objectFit: "contain" }} />
          ) : (
            <div style={{
              width: "44px", height: "44px", borderRadius: "50%",
              background: BRAND.lightGray, display: "flex", alignItems: "center",
              justifyContent: "center", fontSize: "14pt", fontWeight: 700,
              color: BRAND.gray, flexShrink: 0,
            }}>
              {name.charAt(0)}
            </div>
          )}

          <div
            style={{
              display: "flex",
              flexDirection: "column",

            }}>
            <div style={{ fontWeight: 700, fontSize: "17pt" }}>{name}</div>
            {phone && <div style={{ fontWeight: 500, fontSize: "12pt" }}>{phone}</div>}
            {website && <div style={{ fontWeight: 100, fontSize: "10pt" }}>{website}</div>}
            {address && (
              <div style={{ fontWeight: 100, fontSize: "10pt", whiteSpace: "pre-line" }}>{address}</div>
            )}
          </div>

        </div>

        {/* Feature checklist */}
        <div style={{
          background: "radial-gradient(circle,rgba(81, 172, 72, 1) 0%, rgba(9, 64, 4, 1) 100%)", padding: "10px 18px",
          display: "flex", flexDirection: "column", justifyContent: "center", gap: "2px",
        }}>
          {INSPECTION_FEATURES.map((f) => (
            <div key={f} style={{ display: "flex", alignItems: "center", gap: "20px", color: BRAND.white, fontSize: "15pt" }}>
              <span style={{ color: BRAND.greenLight, fontWeight: 700, fontSize: "15pt", paddingLeft: "15pt" }}>✓</span>
              {f}
            </div>
          ))}
        </div>
      </div>
    </div >
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// WindowStickerDetailPage — Window Sticker 3 (spec-sheet style)
//
// Layout (top → bottom):
//   1. Header row — dealer logo + name (left) | green check + SCAN text + QR (right)
//                   Red curved arrow points from SCAN area down toward QR code
//   2. Vehicle title — large bold year/make/model + trim line + mileage
//   3. Spec grid — 2-column list of vehicle attributes
//      Fields currently available from the API are shown with their values.
//      Fields marked "// TODO" need to be added to the Vehicle entity,
//      InspectionDto, GetInspections projection, and web-api-client.ts before
//      they will populate.
//   4. Price box — green border, large black price, disclaimer
//   5. Footer — Q-caR logo + tagline on light gray background
// ─────────────────────────────────────────────────────────────────────────────
export const WindowStickerDetailPage = ({ item, dealerProfile }) => {
  const {
    id,
    vehicleYear = "",
    vehicleMake = "",
    vehicleModel = "",
    vehicleTrim = "",
    vehicleMileage,
    vehiclePrice,
    vehicleVin = "",
    vehicleColor = "",
    vehicleStockNumber = "",
    vehicleCondition = null,
    // TODO — add these fields to Vehicle entity + InspectionDto + GetInspections + web-api-client:
    // vehicleType, vehicleEngine, vehicleTransmission,
    // vehicleDrivetrain, vehicleSeatingRows, vehicleFuelType,
    // vehicleNhtsaRating, vehicleDoors, vehicleMaxSeating
  } = item;

  // Condition is stored as an integer: 0 = New, 1 = Used
  const conditionLabel =
    vehicleCondition === 0 ? "New" :
      vehicleCondition === 1 ? "Used" :
        null;

  const {
    name = "Your Dealership",
    logoUrl = null,
    phone = "",
    website = "",
    address = "",
    slogan = "",
  } = dealerProfile || {};
  const qrUrl = `${window.location.origin}/dvi/scan/${id}`;

  // ── Spec rows: [label, value]
  // Rows with null values show "—" and a note that the field needs backend work.
  const specsLeft = [
    ["Condition", conditionLabel],
    ["Trim", vehicleTrim || null],
    ["Color", vehicleColor || null],
    ["Stock #", vehicleStockNumber || null],

  ];

  const specsRight = [
    ["Vehicle Type", null],                          // TODO: vehicleType
    ["Engine", null],                          // TODO: vehicleEngine
    ["Transmission", null],                          // TODO: vehicleTransmission
    ["Drivetrain", null],                          // TODO: vehicleDrivetrain
    ["Seating Rows", null],                          // TODO: vehicleSeatingRows
    ["Fuel Type", null],                          // TODO: vehicleFuelType
    ["Doors", null],                          // TODO: vehicleDoors
    ["Max Seating", null],                          // TODO: vehicleMaxSeating
  ];

  const SpecItem = ({ label, value }) => (
    <div style={{ display: "flex", gap: "8px", alignItems: "flex-start", marginBottom: "10px", marginLeft: "16px" }}>
      {/* Circle bullet matching the mockup */}
      <div style={{
        width: "14px", height: "14px", borderRadius: "50%",
        border: "2px solid #444", flexShrink: 0, marginTop: "3px",
      }} />
      <div>
        <div style={{ fontSize: "10pt", color: BRAND.gray, lineHeight: 1.15 }}>{label}</div>
        <div style={{
          fontSize: "13pt", fontWeight: 700, lineHeight: 1.15,
          color: value ? BRAND.dark : "#bbb",   /* gray-out missing fields */
        }}>
          {value || "—"}
        </div>
      </div>
    </div>
  );

  return (
    <div
      className="window-sticker-detail-page"
      style={{
        width: "8.5in", height: "11in",
        background: BRAND.white,
        display: "flex", flexDirection: "column",
        fontFamily: "'Segoe UI', Arial, Helvetica, sans-serif",
        color: BRAND.dark, overflow: "hidden", boxSizing: "border-box",
        padding: "0.32in 0.42in 0.25in",
      }}
    >
      {/* ── Zone 1: Header row ── */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "14px" }}>

        {/* Left: dealer logo + name + contact placeholder */}
        <div style={{ maxWidth: "70%" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "8px" }}>
            {logoUrl ? (
              <img src={logoUrl} alt={name}
                style={{ backgroundColor: BRAND.dark, height: "100px", width: "auto", maxWidth: "300px", objectFit: "contain" }} />
            ) : (
              <div style={{
                width: "52px", height: "52px", borderRadius: "50%",
                background: BRAND.lightGray, display: "flex", alignItems: "center",
                justifyContent: "center", fontSize: "14pt", fontWeight: 700,
                color: BRAND.gray, flexShrink: 0,
              }}>
                {name.charAt(0)}
              </div>
            )}
          </div>

          {/* Contact info — now below the logo row, left aligned */}
          <div style={{ fontWeight: 700, fontSize: "12pt" }}>{name}</div>
          {slogan && (
            <div style={{ fontWeight: 400, fontSize: "10pt", color: BRAND.gray, fontStyle: "italic" }}>{slogan}</div>
          )}
          <div style={{ color: BRAND.dark, textAlign: "left", lineHeight: 1.25 }}>
            {phone && <div style={{ fontWeight: 500, fontSize: "12pt" }}>{phone}</div>}
            {website && <div style={{ fontWeight: 300, fontSize: "10pt" }}>{website}</div>}
            {address && (
              <div style={{ fontWeight: 300, fontSize: "10pt", whiteSpace: "pre-line" }}>{address}</div>
            )}
          </div>
        </div>

        {/* Right: SCAN CTA only.
            QR is intentionally NOT on this page — Detail is a spec sheet; the
            QR rides on a separate page when the user prints "+ QR sheet".
            Removed the red flourish arrow: it had no target on-page and was
            distracting. The green check + bold SCAN text reads as a CTA on
            its own. */}
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
          <div style={{ display: "flex", alignItems: "flex-start", gap: "30px" }}>
            <CheckIcon size={150} />
            <div style={{ lineHeight: 1.2, alignItems: "start" }}>
              <div style={{ fontSize: "40pt", fontWeight: 900, color: BRAND.green }}>SCAN</div>
              <div style={{ fontSize: "22pt", fontWeight: 400 }}>before</div>
              <div style={{ fontSize: "35pt", fontWeight: 900 }}>YOU</div>
              <div style={{ fontSize: "35pt", fontWeight: 900 }}>BUY</div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Zone 2: Vehicle title ── */}
      <div style={{ marginBottom: "12px", marginTop: "12px" }}>
        <div style={{ fontSize: "40pt", fontWeight: 900, lineHeight: 1.1 }}>
          {[vehicleYear, vehicleMake, vehicleModel].filter(Boolean).join(" ")}
        </div>
        <div style={{ fontSize: "20pt", fontWeight: 700, marginTop: "2px", color: BRAND.dark }}>
          {vehicleTrim || ""}
        </div>
        {vehicleMileage && (
          <div style={{ fontSize: "20pt", fontWeight: 700, color: BRAND.dark }}>
            {fmt.miles(vehicleMileage)}
          </div>
        )}
      </div>

      {/* ── Zone 3: Spec grid (full width — no inline QR per Design 2) ── */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 28px", marginBottom: "14px" }}>
        <div>{specsLeft.map(([label, value]) => <SpecItem key={label} label={label} value={value} />)}</div>
        <div>{specsRight.map(([label, value]) => <SpecItem key={label} label={label} value={value} />)}</div>
      </div>

      {/* ── Zone 4: Price (plain bold per Design 2 — no border box) ── */}
      <div style={{
        textAlign: "center", padding: "4px 20px",
        margin: "0 auto 6px",
        flexShrink: 0,
      }}>
        <div style={{ fontSize: "72pt", fontWeight: 900, lineHeight: 1, letterSpacing: "-0.02em" }}>
          {fmt.price(vehiclePrice)}
        </div>
        <div style={{ fontSize: "8.5pt", color: BRAND.gray, marginTop: "4px" }}>
          *Tax, title and fees not included
        </div>
      </div>

      {/* ── Zone 5: Q-caR footer ── */}
      <div style={{
        borderRadius: "6px", textAlign: "center", flexShrink: 0,
      }}>
        <img
          src="logo-dark.png"
          alt="qcar logo"
          style={{
            width: `2.2in`,
            maxWidth: "100%",
            height: "auto",
            display: "block",
            margin: "8px auto 4px",
          }}
        />
        {/* <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "10px", marginBottom: "3px" }}>
          <CheckIcon size={34} />
          <span style={{ fontSize: "24pt", fontWeight: 700 }}>
            Q-ca<span style={{ color: BRAND.green }}>R</span>
          </span>
        </div>
        <div style={{ fontSize: "10pt", color: BRAND.gray }}>
          Built on Transparency. Driven by Trust.
        </div> */}
      </div>
    </div >
  );
};


// ─────────────────────────────────────────────────────────────────────────────
// WindowStickerAltPage — alternate full-page sticker (hero image style)
//
// Layout (top → bottom):
//   1. Hero banner  — dealer-hero.png bg + dark overlay, vehicle info left,
//                     dealer logo/name right, Q-caR logo top-right corner
//   2. "Powered By Q-caR" strip
//   3. "SCAN BEFORE YOU BUY" strip with large green check + green underline
//   4. Large QR code (QRCodeCanvas — same value as QR sheet, identical on screen & print)
//   5. Price in large red text + disclaimer
//
// dealer-hero.png lives in ClientApp/public and is rendered as a positioned <img>
// so it always prints (no dependency on "Background graphics" being enabled).
// ─────────────────────────────────────────────────────────────────────────────
export const WindowStickerAltPage = ({ item, dealerProfile }) => {
  const {
    id,
    vehicleYear = "",
    vehicleMake = "",
    vehicleModel = "",
    vehicleTrim = "",
    vehicleMileage,
    vehiclePrice,
  } = item;

  const {
    name = "Your Dealership",
    logoUrl = null,
    phone = "",
    website = "",
    address = "",
    slogan = "",
  } = dealerProfile || {};
  const qrUrl = `${window.location.origin}/dvi/scan/${id}`;

  return (
    <div
      className="window-sticker-alt-page"
      style={{
        width: "8.5in", height: "11in",
        background: BRAND.white,
        display: "flex", flexDirection: "column",
        fontFamily: "'Segoe UI', Arial, Helvetica, sans-serif",
        color: BRAND.dark, overflow: "hidden", boxSizing: "border-box",
      }}
    >
      {/* ── Zone 1: Hero banner ── */}
      <div style={{ position: "relative", height: "2.5in", flexShrink: 0, overflow: "hidden" }}>
        {/* Background photo — always prints as an <img>, no CSS background needed */}
        <img src="dealer-hero.png" alt=""
          style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", objectPosition: "40% 30%" }}
        />
        {/* Dark overlay */}
        <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.60)" }} />

        {/* Content: vehicle info (left) + dealer info (right) */}
        <div style={{
          position: "relative", zIndex: 1, height: "100%",
          display: "flex", justifyContent: "space-between",
          padding: "24px 32px 20px", gap: "16px"
        }}>
          {/* Left: big vehicle name, red rule, trim + mileage */}
          <div style={{ display: "flex", flexDirection: "column", justifyContent: "center" }}>
            <div style={{ fontSize: "35pt", fontWeight: 900, color: BRAND.white, lineHeight: 1.05, maxWidth: "4.5in" }}>
              {[vehicleYear, vehicleMake, vehicleModel].filter(Boolean).join(" ")}
            </div>
            <div style={{ width: "100%", height: "5px", background: BRAND.red, margin: "8px 0 6px" }} />
            <div style={{ fontSize: "20pt", fontWeight: "500", color: BRAND.white, lineHeight: 1.5 }}>
              {vehicleTrim && <div>{vehicleTrim}</div>}
              {vehicleMileage && <div>{fmt.miles(vehicleMileage)}</div>}
            </div>
          </div>

          {/* Right: Q-caR logo pinned top-right + dealer below */}
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", justifyContent: "flex-end", flexShrink: 0, minWidth: "1.8in", gap: "0px" }}>
            {/* <img src="logo-dark.png" alt="Q-caR"
              style={{ height: "28px", width: "auto", objectFit: "contain", filter: "brightness(0) invert(1)" }}
            /> */}
            <div style={{ textAlign: "right" }}>
              {logoUrl ? (
                // Render the dealer's logo in its uploaded colors. No CSS
                // filter — the old `brightness(0) invert(1)` worked for the
                // monochrome placeholder Q-caR mark, but crushes a real
                // dealer logo into an invisible white blob.
                <img src={logoUrl} alt={name}
                  style={{ height: "80px", maxWidth: "130px", objectFit: "contain", verticalAlign: "bottom", objectPosition: "right" }}
                />
              ) : (
                <div style={{ fontSize: "12pt", fontWeight: 700, color: BRAND.white }}>{name}</div>
              )}
            </div>

            <div style={{ color: BRAND.white, textAlign: "right", marginTop: "-15px" }}>
              {phone && <div style={{ fontWeight: 500, fontSize: "12pt" }}>{phone}</div>}
              {website && <div style={{ fontWeight: 300, fontSize: "10pt" }}>{website}</div>}
              {address && (
                <div style={{ fontWeight: 300, fontSize: "10pt", whiteSpace: "pre-line" }}>{address}</div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── Zone 2: "Powered By Q-caR" strip ── */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "10px", padding: "30px 24px 6px", flexShrink: 0 }}>
        <span style={{ fontSize: "20pt", fontStyle: "italic", fontWeight: 600 }}>Powered By</span>
        <img src="logo-dark.png" style={{ width: "130pt" }} />
        {/* <CheckIcon size={50} /> */}
        {/* <div style={{ lineHeight: 1.1 }}>
          <span style={{ fontSize: "18pt", fontWeight: 700 }}>Q-ca<span style={{ color: BRAND.green }}>R</span></span>
          <div style={{ fontSize: "6.5pt", color: BRAND.gray }}>Built on Transparency. Driven by Trust.</div>
        </div> */}
      </div>

      {/* ── Zone 3: SCAN BEFORE YOU BUY ── */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "center",
        gap: "14px", padding: "6px 32px 20px",
        borderBottom: `3px solid ${BRAND.green}`, flexShrink: 0,
      }}>
        <CheckIcon size={58} />
        <div style={{ display: "flex", alignItems: "baseline", gap: "10px" }}>
          <span style={{ fontSize: "40pt", fontWeight: 900, color: BRAND.green, lineHeight: 1 }}>SCAN</span>
          <span style={{ fontSize: "36pt", fontWeight: 400, lineHeight: 1 }}>BEFORE</span>
          <span style={{ fontSize: "40pt", fontWeight: 900, lineHeight: 1 }}>YOU BUY</span>
        </div>
      </div>

      {/* ── Zone 4: QR code ── */}
      <div style={{ flex: "1 1 auto", display: "flex", alignItems: "center", justifyContent: "center", padding: "12px 32px", minHeight: 0 }}>
        <QRCodeCanvas value={qrUrl} size={400} />
      </div>

      {/* ── Zone 5: Price (large red) + disclaimer ── */}
      <div style={{ textAlign: "center", padding: "2px 12px 40px", flexShrink: 0 }}>
        <div style={{ fontSize: "75pt", fontWeight: 900, color: BRAND.red, lineHeight: 1, letterSpacing: "-0.02em" }}>
          {fmt.price(vehiclePrice)}
        </div>
        <div style={{ fontSize: "8.5pt", color: BRAND.gray, marginTop: "4px" }}>
          *Tax, title and fees not included
        </div>
      </div>
    </div>
  );
};


// ─────────────────────────────────────────────────────────────────────────────
// WindowStickerModernPage — Window Sticker 4 (bold red / black style)
//
// Layout (top → bottom):
//   1. Black header  — dealer logo + name (white) left; red triangle accent right
//   2. Two-column content area:
//      Left  — red-bordered vehicle title box + spec grid (left red line)
//      Right — green check + SCAN text + red curved arrow + QR code
//   3. Price box — thick red border, fit-content width, centred
//   4. Footer — "Powered By Q-caR" left | black bg dealer contact right
// ─────────────────────────────────────────────────────────────────────────────
export const WindowStickerModernPage = ({ item, dealerProfile }) => {
  const {
    id,
    vehicleYear = "",
    vehicleMake = "",
    vehicleModel = "",
    vehicleTrim = "",
    vehicleMileage,
    vehiclePrice,
    vehicleColor = "",
    vehicleStockNumber = "",
    vehicleCondition = null,
  } = item;

  const conditionLabel =
    vehicleCondition === 0 ? "New" :
      vehicleCondition === 1 ? "Used" :
        null;

  const {
    name = "Your Dealership",
    logoUrl = null,
    phone = "",
    website = "",
    address = "",
    slogan = "",
  } = dealerProfile || {};
  const qrUrl = `${window.location.origin}/dvi/scan/${id}`;

  const specsLeft = [
    ["Condition", conditionLabel],
    ["Trim", vehicleTrim || null],
    ["Color", vehicleColor || null],
    ["Stock #", vehicleStockNumber || null],
  ];

  const specsRight = [
    ["Vehicle Type", null],                    // TODO: vehicleType
    ["Engine", null],                    // TODO: vehicleEngine
    ["Transmission", null],                    // TODO: vehicleTransmission
    ["Drivetrain", null],                    // TODO: vehicleDrivetrain
    ["Seating Rows", null],                    // TODO: vehicleSeatingRows
    ["Fuel Type", null],                    // TODO: vehicleFuelType
    ["Doors", null],                    // TODO: vehicleDoors
  ];

  const SpecItem = ({ label, value }) => (
    <div style={{ display: "flex", gap: "8px", alignItems: "flex-start", marginBottom: "8px", marginLeft: "16px" }}>
      <div style={{
        width: "14px", height: "14px", borderRadius: "50%",
        border: "2px solid #333", flexShrink: 0, marginTop: "3px",
      }} />
      <div>
        <div style={{ fontSize: "10pt", color: BRAND.gray, lineHeight: 1.1 }}>{label}</div>
        <div style={{ fontSize: "13pt", fontWeight: 700, lineHeight: 1.15, color: value ? BRAND.dark : "#ccc" }}>
          {value || "—"}
        </div>
      </div>
    </div>
  );

  return (
    <div
      className="window-sticker-modern-page"
      style={{
        width: "8.5in", height: "11in",
        background: BRAND.white,
        display: "flex", flexDirection: "column",
        fontFamily: "'Segoe UI', Arial, Helvetica, sans-serif",
        color: BRAND.dark, overflow: "hidden", boxSizing: "border-box",
      }}
    >
      {/* ── Zone 1: Black header ── */}
      <div style={{
        background: "#111",
        padding: "0px 28px",
        display: "flex", alignItems: "center",
        position: "relative", overflow: "hidden", flexShrink: 0,
      }}>
        {/* Dealer logo + name */}
        <div style={{ display: "flex", alignItems: "center", gap: "14px", zIndex: 1 }}>
          {logoUrl ? (
            // Render the dealer's logo in its uploaded colors. No CSS filter
            // — see comment on the matching Hero block above.
            <img src={logoUrl} alt={name}
              style={{ height: "150px", width: "auto", maxWidth: "200px", objectFit: "contain" }}
            />
          ) : (
            <div style={{
              width: "58px", height: "58px", borderRadius: "50%",
              background: BRAND.red, display: "flex", alignItems: "center",
              justifyContent: "center", fontSize: "20pt", fontWeight: 900, color: BRAND.white,
            }}>
              {name.charAt(0)}
            </div>
          )}
          <span style={{ color: BRAND.white, fontWeight: 900, fontSize: "40pt", letterSpacing: "0.03em" }}>
            {name.toUpperCase()}
          </span>
        </div>
        {/* Red triangle accent — top right */}
        <div style={{
          position: "absolute", right: 0, top: 0,
          width: "160px", height: "100%",
          background: BRAND.red,
          clipPath: "polygon(55% 0, 100% 0, 100% 100%, 100% 100%)",
        }} />
      </div>

      {/* ── Zone 2: Two-column content ── */}
      <div style={{
        flex: "1 1 auto", display: "flex", flexDirection: "row",
        padding: "18px 28px 8px", gap: "18px", minHeight: 0,
      }}>

        {/* Left: title box + spec grid */}
        <div style={{ flex: "1 1 auto", display: "flex", flexDirection: "column", minWidth: 0 }}>

          {/* Red-bordered vehicle title */}
          <div style={{
            border: `10px solid ${BRAND.red}`, borderRadius: "4px",
            padding: "8px 12px", marginBottom: "10px", flexShrink: 0,
          }}>
            <div style={{ fontSize: "24pt", fontWeight: 900, lineHeight: 1.05 }}>
              {[vehicleYear, vehicleMake, vehicleModel].filter(Boolean).join(" ")}
            </div>
            {vehicleTrim && (
              <div style={{ fontSize: "13pt", fontWeight: 700, marginTop: "2px" }}>{vehicleTrim}</div>
            )}
            {vehicleMileage && (
              <div style={{ fontSize: "13pt", fontWeight: 700 }}>{fmt.miles(vehicleMileage)}</div>
            )}
          </div>

          {/* Spec grid — left red line */}
          <div style={{ borderLeft: `4px solid ${BRAND.red}`, paddingLeft: "14px", flex: "1 1 auto" }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px" }}>
              <div>{specsLeft.map(([label, value]) => <SpecItem key={label} label={label} value={value} />)}</div>
              <div>{specsRight.map(([label, value]) => <SpecItem key={label} label={label} value={value} />)}</div>
            </div>
          </div>
        </div>

        {/* Right: SCAN CTA only.
            QR is intentionally NOT here — Modern is a spec sheet; the QR
            rides on a separate page when the user prints "+ QR sheet".
            Removed the red flourish arrow: it had no on-page target and was
            distracting. The green check + stacked SCAN text reads on its own. */}
        <div style={{
          width: "2.3in", flexShrink: 0,
          display: "flex", flexDirection: "column", alignItems: "center",
          paddingTop: "2px",
        }}>
          <div style={{ display: "flex", alignItems: "flex-start", gap: "8px", marginBottom: "6px" }}>
            <CheckIcon size={52} />
            <div style={{ lineHeight: 1.0 }}>
              <div style={{ fontSize: "26pt", fontWeight: 900, color: BRAND.green }}>SCAN</div>
              <div style={{ fontSize: "18pt", fontWeight: 400 }}>before</div>
              <div style={{ fontSize: "23pt", fontWeight: 900 }}>YOU</div>
              <div style={{ fontSize: "23pt", fontWeight: 900 }}>BUY</div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Zones 3 + 4: price band + footer ──
          Layered layout per Design 3:
          • Tilted-top black band runs full width
          • Large red parallelogram overlays the right ~55% of the band
          • Price box floats centered, partially above + dipping into the band
          • Dealer contact info sits inside the band on the right side, in white
          • "Powered By Q-caR" goes below the band on the white footer */}
      <div style={{ flexShrink: 0, paddingTop: "0.2in" }}>

        {/* Layered band: black slab + red wedge + price box + dealer info */}
        <div style={{ position: "relative", height: "2.4in" }}>

          {/* Black slab — top edge tilted (lower-left, higher-right) */}
          <div style={{
            position: "absolute", left: 0, right: 0, top: 0, bottom: 0,
            background: "#111",
            clipPath: "polygon(0 32%, 100% 12%, 100% 100%, 0 100%)",
            zIndex: 0,
          }} />

          {/* Red parallelogram wedge — overlays the right portion of the band */}
          <div style={{
            position: "absolute", right: 0, top: 0, bottom: 0,
            width: "55%",
            background: BRAND.red,
            clipPath: "polygon(28% 18%, 100% 0%, 100% 100%, 0 100%)",
            zIndex: 1,
          }} />

          {/* Price box — centered, sticks up above the black band */}
          <div style={{
            position: "absolute",
            top: "0", left: "50%", transform: "translateX(-50%)",
            background: BRAND.white, border: `8px solid ${BRAND.red}`,
            borderRadius: "4px", padding: "6px 40px", textAlign: "center",
            zIndex: 10,
          }}>
            <div style={{ fontSize: "70pt", fontWeight: 900, lineHeight: 1, letterSpacing: "-0.02em" }}>
              {fmt.price(vehiclePrice)}
            </div>
            <div style={{ fontSize: "7.5pt", color: BRAND.gray, marginTop: "2px" }}>
              *Tax, title and fees not included
            </div>
          </div>

          {/* Dealer contact — inside the band, bottom-right, white text */}
          <div style={{
            position: "absolute",
            right: "0.4in", bottom: "0.25in",
            color: BRAND.white, textAlign: "left",
            display: "flex", flexDirection: "column", gap: "4px",
            zIndex: 5,
          }}>
            {phone && (
              <div style={{ display: "flex", alignItems: "center", gap: "10px", fontSize: "13pt", fontWeight: 700 }}>
                <span>☎</span><span>{phone}</span>
              </div>
            )}
            {address && (
              <div style={{ display: "flex", alignItems: "flex-start", gap: "10px", fontSize: "11pt", whiteSpace: "pre-line" }}>
                <span>📍</span><span>{address}</span>
              </div>
            )}
            {website && (
              <div style={{ display: "flex", alignItems: "center", gap: "10px", fontSize: "10pt", color: "#eaeaea" }}>
                <span>🌐</span><span>{website}</span>
              </div>
            )}
          </div>
        </div>

        {/* Bottom strip on white — "Powered By Q-caR" */}
        <div style={{
          padding: "14px 28px 18px",
          display: "flex", alignItems: "center", gap: "14px",
        }}>
          <div style={{ fontSize: "18pt", fontStyle: "italic", fontWeight: 700 }}>Powered By</div>
          <img
            src="logo-dark.png"
            alt="qcar logo"
            style={{
              width: "2.2in",
              maxWidth: "100%",
              height: "auto",
              display: "block",
            }}
          />
        </div>

      </div>
    </div >
  );
};


// ─────────────────────────────────────────────────────────────────────────────
// QrOnlyPage — one full letter-page QR sheet
// ─────────────────────────────────────────────────────────────────────────────
const QrOnlyPage = ({ item }) => {
  const url = `${window.location.origin}/dvi/scan/${item.id}`;
  return (
    <div
      style={{
        width: "8.5in",
        height: "11in",
        background: BRAND.white,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "0.5in",
        fontFamily: "'Segoe UI', Arial, Helvetica, sans-serif",
        boxSizing: "border-box",
      }}
    >
      <div style={{ width: "100%", display: "grid", placeItems: "center" }}>
        <PrintSafeQR url={url} />
      </div>
      <div style={{ marginTop: "16px", textAlign: "center" }}>
        <div style={{ fontWeight: 700, fontSize: "16pt" }}>
          {item.vehicleLabel || `Inspection #${item.id}`}
        </div>
        <div style={{ fontSize: "12pt", color: BRAND.gray }}>{item.vehicleVin || "—"}</div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Print CSS
// ─────────────────────────────────────────────────────────────────────────────
const BASE_PRINT_CSS = `
  html, body {
    margin: 0 !important; padding: 0 !important;
    background: #fff !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
    font-family: "Segoe UI", Arial, Helvetica, sans-serif !important;
  }
  *, *::before, *::after { box-sizing: border-box; }
  .no-print   { display: none !important; }
  .print-page { break-after: avoid; page-break-after: avoid; }
  .print-page:last-child { break-after: avoid !important; page-break-after: avoid !important; }
  .screen-only { display: none !important; }
  .print-only  { display: block !important; }
`;

const QR_PRINT_CSS = `@page { size: Letter portrait; margin: 0; } ${BASE_PRINT_CSS}`;

const STICKER_PRINT_CSS = `
  @page { size: Letter portrait; margin: 0; }
  ${BASE_PRINT_CSS}
  .window-sticker-page { width: 8.5in !important; height: 11in !important; overflow: hidden !important; }
`;

const BOTH_PRINT_CSS = `
  @page { size: Letter portrait; margin: 0; }
  ${BASE_PRINT_CSS}
  .window-sticker-page { width: 8.5in !important; height: 11in !important; overflow: hidden !important; }
  .qr-print-wrapper {
    width: 8.5in; min-height: 11in;
    display: flex; flex-direction: column;
    align-items: center; justify-content: center; padding: 0;
  }
`;

const DETAIL_STICKER_PRINT_CSS = `
  @page { size: Letter portrait; margin: 0; }
  ${BASE_PRINT_CSS}
  .window-sticker-detail-page { width: 8.5in !important; height: 11in !important; overflow: hidden !important; }
`;

const BOTH_DETAIL_PRINT_CSS = `
  @page { size: Letter portrait; margin: 0; }
  ${BASE_PRINT_CSS}
  .window-sticker-detail-page { width: 8.5in !important; height: 11in !important; overflow: hidden !important; }
  .qr-print-wrapper {
    width: 8.5in; min-height: 11in;
    display: flex; flex-direction: column;
    align-items: center; justify-content: center; padding: 0;
  }
`;

const ALT_STICKER_PRINT_CSS = `
  @page { size: Letter portrait; margin: 0; }
  ${BASE_PRINT_CSS}
  .window-sticker-alt-page { width: 8.5in !important; height: 11in !important; overflow: hidden !important; }
`;

const BOTH_ALT_PRINT_CSS = `
  @page { size: Letter portrait; margin: 0; }
  ${BASE_PRINT_CSS}
  .window-sticker-alt-page { width: 8.5in !important; height: 11in !important; overflow: hidden !important; }
  .qr-print-wrapper {
    width: 8.5in; min-height: 11in;
    display: flex; flex-direction: column;
    align-items: center; justify-content: center; padding: 0;
  }
`;

const MODERN_STICKER_PRINT_CSS = `
  @page { size: Letter portrait; margin: 0; }
  ${BASE_PRINT_CSS}
  .window-sticker-modern-page { width: 8.5in !important; height: 11in !important; overflow: hidden !important; }
`;

const BOTH_MODERN_PRINT_CSS = `
  @page { size: Letter portrait; margin: 0; }
  ${BASE_PRINT_CSS}
  .window-sticker-modern-page { width: 8.5in !important; height: 11in !important; overflow: hidden !important; }
  .qr-print-wrapper {
    width: 8.5in; min-height: 11in;
    display: flex; flex-direction: column;
    align-items: center; justify-content: center; padding: 0;
  }
`;


// ─────────────────────────────────────────────────────────────────────────────
// Template catalog — drives the picker, the preview, and the print dispatch.
// ─────────────────────────────────────────────────────────────────────────────
const TEMPLATES = [
  { id: "qr",      label: "QR Only", variant: "primary", tone: "#0d6efd" },
  { id: "classic", label: "Classic", variant: "success", tone: "#198754" },
  { id: "hero",    label: "Hero",    variant: "warning", tone: "#ffc107" },
  { id: "detail",  label: "Detail",  variant: "info",    tone: "#0dcaf0" },
  { id: "modern",  label: "Modern",  variant: "danger",  tone: "#dc3545" },
];

const TEMPLATE_STORAGE_KEY = "qcar.printQrs.template";
const INCLUDE_QR_STORAGE_KEY = "qcar.printQrs.includeQR";

// ─────────────────────────────────────────────────────────────────────────────
// PreviewToolbar — sticky toolbar in Step 2
// Replaces the old 9-button row. Surfaces one decision at a time:
//   (1) which template to print, (2) optionally bundle the QR sheet,
//   (3) hit Print.
// ─────────────────────────────────────────────────────────────────────────────
const PreviewToolbar = ({
  count,
  templateId,
  onTemplateChange,
  includeQR,
  onIncludeQRChange,
  onBack,
  onPrint,
}) => {
  const selected = TEMPLATES.find((t) => t.id === templateId) || TEMPLATES[0];
  const isQrOnly = selected.id === "qr";

  return (
    <div
      className="no-print preview-toolbar-dark"
      style={{
        position: "sticky",
        top: 0,
        zIndex: 100,
        background: "#1f1f1f",
        color: "#f1f3f5",
        borderBottom: "1px solid #444",
        padding: "12px 24px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        flexWrap: "wrap",
        gap: "16px",
        boxShadow: "0 2px 8px rgba(0,0,0,0.45)",
      }}
    >
      {/* Scoped style: switch label / form-check colors for dark surface */}
      <style>{`
        .preview-toolbar-dark .form-check-label { color: #f1f3f5; }
        .preview-toolbar-dark .form-check-input { background-color: #343a40; border-color: #6c757d; }
        .preview-toolbar-dark .form-check-input:checked { background-color: #0d6efd; border-color: #0d6efd; }
      `}</style>

      {/* Left: back + context */}
      <div style={{ display: "flex", alignItems: "center", gap: "12px", flexShrink: 0 }}>
        <Button variant="outline-light" size="sm" onClick={onBack}>← Back</Button>
        <span style={{ fontWeight: 600, fontSize: "15px", color: "#f1f3f5" }}>
          Preview — {count} vehicle{count !== 1 ? "s" : ""}
        </span>
      </div>

      {/* Center: template picker (segmented control) */}
      <div
        role="group"
        aria-label="Choose template"
        style={{ display: "flex", alignItems: "center", gap: "6px", flexWrap: "wrap" }}
      >
        <span style={{ fontSize: "13px", color: "#adb5bd", marginRight: "4px" }}>Template:</span>
        {TEMPLATES.map((t) => {
          const isSelected = t.id === templateId;
          return (
            <Button
              key={t.id}
              size="sm"
              variant={isSelected ? t.variant : `outline-${t.variant}`}
              onClick={() => onTemplateChange(t.id)}
              aria-pressed={isSelected}
            >
              {t.label}
            </Button>
          );
        })}
      </div>

      {/* Right: include-QR toggle + Print */}
      <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
        {!isQrOnly && (
          <Form.Check
            type="switch"
            id="include-qr-switch"
            label="Include QR sheet"
            checked={includeQR}
            onChange={(e) => onIncludeQRChange(e.target.checked)}
            style={{ fontSize: "13px", userSelect: "none", color: "#f1f3f5" }}
          />
        )}
        <Button variant={selected.variant} onClick={onPrint}>
          Print {selected.label}
          {!isQrOnly && includeQR ? " + QR" : ""}
        </Button>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// SideBySidePreview — scaled thumbnail previews, screen only
// ─────────────────────────────────────────────────────────────────────────────
const SCALE = 0.42; // default shrink factor for thumbnails
const PAGE_W = 8.5 * 96; // 816px — letter width in px at 96dpi
const PAGE_H = 11 * 96; // 1056px

const PageThumbnail = ({ label, children, scale = SCALE }) => (
  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "8px" }}>
    {/* Outer frame — fixed screen size */}
    <div
      style={{
        width: `${PAGE_W * scale}px`,
        height: `${PAGE_H * scale}px`,
        boxShadow: "0 4px 20px rgba(0,0,0,0.14)",
        borderRadius: "6px",
        overflow: "hidden",
        position: "relative",
        background: "white",
      }}
    >
      {/* Inner content scaled down */}
      <div
        style={{
          transform: `scale(${scale})`,
          transformOrigin: "top left",
          width: `${PAGE_W}px`,
          height: `${PAGE_H}px`,
          pointerEvents: "none",
        }}
      >
        {children}
      </div>
    </div>
    {label && (
      <span style={{ fontSize: "12px", fontWeight: 500, color: "#6c757d" }}>{label}</span>
    )}
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// SingleTemplatePreview — one preview per vehicle, in the selected template.
// Replaces the old SideBySidePreview which rendered all 5 variants per vehicle.
// ─────────────────────────────────────────────────────────────────────────────
const renderTemplateBody = (templateId, item, dealerProfile) => {
  if (templateId === "classic") return <WindowStickerPage item={item} dealerProfile={dealerProfile} />;
  if (templateId === "hero")    return <WindowStickerAltPage item={item} dealerProfile={dealerProfile} />;
  if (templateId === "detail")  return <WindowStickerDetailPage item={item} dealerProfile={dealerProfile} />;
  if (templateId === "modern")  return <WindowStickerModernPage item={item} dealerProfile={dealerProfile} />;

  // QR Only fallback — same markup used by the existing hidden print area
  const url = `${window.location.origin}/dvi/scan/${item.id}`;
  return (
    <div className="desktop-print-preview" style={{ width: "100%", height: "100%" }}>
      <div className="qr-fullpage">
        <div className="qr-box">
          <PrintSafeQR url={url} />
        </div>
        <div className="qr-meta">
          <div className="qr-title">
            {item.vehicleLabel || `Inspection #${item.id}`}
          </div>
          <div>{item.vehicleVin || "—"}</div>
        </div>
      </div>
    </div>
  );
};

const SingleTemplatePreview = ({ selectedList, dealerProfile, templateId }) => (
  <div style={{ padding: "32px 24px", display: "flex", flexDirection: "column", gap: "40px", alignItems: "center" }}>
    {selectedList.map((item, idx) => (
      <div key={item.id} style={{ width: "100%", display: "flex", flexDirection: "column", alignItems: "center" }}>
        {/* Vehicle heading */}
        <div style={{ marginBottom: "12px", textAlign: "center" }}>
          <div style={{ fontWeight: 600, fontSize: "16px", color: "#f1f3f5" }}>
            {[item.vehicleYear, item.vehicleMake, item.vehicleModel].filter(Boolean).join(" ") || `Inspection #${item.id}`}
          </div>
          {(item.vehicleVin || item.vehicleStockNumber) && (
            <div style={{ fontSize: "13px", color: "#adb5bd", marginTop: "2px" }}>
              {item.vehicleStockNumber && <span>Stock #{item.vehicleStockNumber}</span>}
              {item.vehicleStockNumber && item.vehicleVin && <span style={{ margin: "0 8px" }}>·</span>}
              {item.vehicleVin && <span>{item.vehicleVin}</span>}
            </div>
          )}
          {selectedList.length > 1 && (
            <div style={{ fontSize: "12px", color: "#6c757d", marginTop: "4px" }}>
              {idx + 1} of {selectedList.length}
            </div>
          )}
        </div>

        <PageThumbnail label={null} scale={0.72}>
          {renderTemplateBody(templateId, item, dealerProfile)}
        </PageThumbnail>
      </div>
    ))}
  </div>
);

const QrBatchPrint = () => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [inspections, setInspections] = useState([]);
  const [selectedMap, setSelectedMap] = useState({});
  const [error, setError] = useState(null);
  const user = React.useContext(UserContext);

  const inspectionsClient = useMemo(() => new InspectionsClient(), []);
  const dealersClient = useMemo(() => new DealersClient(), []);

  // ── Fetch dealer profile (name + logo from the /api/Dealers endpoint) ──────
  const [dealerProfile, setDealerProfile] = useState(null);
  useEffect(() => {
    let active = true;
    dealersClient.getDealerInfo(user?.dealerId ?? null).then((dto) => {
      if (!active) return;
      setDealerProfile({
        name: dto.name || "",
        logoUrl: dto.logoUri || null,
        phone: dto.phone || "",
        website: dto.website || "",
        address: dto.address || "",
        slogan: dto.slogan || "",
      });
    }).catch(() => {/* non-critical — sticker still renders with fallbacks */ });
    return () => { active = false; };
  }, [dealersClient, user?.dealerId]);

  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const [sortColumn, setSortColumn] = useState("inspectiondate");
  const [sortDirection, setSortDirection] = useState("desc");

  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const mqW = window.matchMedia("(max-width: 768px)");
    const mqP = window.matchMedia("(pointer: coarse)");

    const compute = () => setIsMobile(!!(mqW.matches || mqP.matches));
    compute();

    const onChange = () => compute();

    if (mqW.addEventListener) mqW.addEventListener("change", onChange);
    else mqW.addListener(onChange);

    if (mqP.addEventListener) mqP.addEventListener("change", onChange);
    else mqP.addListener(onChange);

    return () => {
      if (mqW.removeEventListener) mqW.removeEventListener("change", onChange);
      else mqW.removeListener(onChange);

      if (mqP.removeEventListener) mqP.removeEventListener("change", onChange);
      else mqP.removeListener(onChange);
    };
  }, []);

  const loadInspections = useCallback(async () => {
    if (!user?.dealerId) return;

    setLoading(true);
    setError(null);

    try {
      const result = await inspectionsClient.getInspectionList(
        user.dealerId,
        pageNumber,
        pageSize,
        search || undefined,
        sortColumn,
        sortDirection,
        statusFilter === "all" ? undefined : Number(statusFilter)
      );

      const normalized = (result?.items || []).map((x) => ({
        id: x.id,
        vehicleLabel: x.vehicleLabel || "",
        vehicleYear: x.vehicleYear || "",
        vehicleMake: x.vehicleMake || "",
        vehicleModel: x.vehicleModel || "",
        vehicleStockNumber: x.vehicleStockNumber || "",
        vehicleVin: x.vehicleVin || x.vehicleLabel || "",
        vehicleColor: x.vehicleColor || "",
        vehicleTrim: x.vehicleTrim || "",
        vehicleMileage: x.vehicleMileage || null,
        vehiclePrice: x.vehiclePrice ?? null,
        vehicleCondition: x.vehicleCondition ?? null,
        status: x.status,
        inspectionDate: x.inspectionDate ? new Date(x.inspectionDate) : null,
        inspectorName: x.inspectorName || "",
      }));

      setInspections(normalized);
      setTotalCount(result?.totalCount ?? normalized.length);
      setTotalPages(result?.totalPages ?? 1);

      if (result?.pageNumber && result.pageNumber !== pageNumber) {
        setPageNumber(result.pageNumber);
      }
    } catch (e) {
      console.error(e);
      setError("Failed to load inspections.");
      setInspections([]);
      setTotalCount(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [
    inspectionsClient,
    user?.dealerId,
    pageNumber,
    pageSize,
    search,
    sortColumn,
    sortDirection,
    statusFilter,
  ]);

  useEffect(() => {
    loadInspections();
  }, [loadInspections]);

  const debouncedSearch = useMemo(
    () =>
      debounce((value) => {
        setPageNumber(1);
        setSearch(value);
      }, 300),
    []
  );

  useEffect(() => {
    return () => debouncedSearch.cancel();
  }, [debouncedSearch]);

  const selectedIds = useMemo(
    () => new Set(Object.keys(selectedMap).map((x) => Number(x))),
    [selectedMap]
  );

  const toggleOne = (row) => {
    setSelectedMap((prev) => {
      const next = { ...prev };
      if (next[row.id]) {
        delete next[row.id];
      } else {
        next[row.id] = row;
      }
      return next;
    });
  };

  const toggleAllOnPage = () => {
    const allSelected =
      inspections.length > 0 && inspections.every((x) => selectedIds.has(x.id));

    setSelectedMap((prev) => {
      const next = { ...prev };

      if (allSelected) {
        inspections.forEach((x) => {
          delete next[x.id];
        });
      } else {
        inspections.forEach((x) => {
          next[x.id] = x;
        });
      }

      return next;
    });
  };

  const clearAll = () => setSelectedMap({});
  const canProceed = selectedIds.size > 0;

  const goBack = () => setStep(1);

  const selectedList = useMemo(() => {
    return Object.values(selectedMap).sort((a, b) => {
      const aDate = a?.inspectionDate ? new Date(a.inspectionDate).getTime() : 0;
      const bDate = b?.inspectionDate ? new Date(b.inspectionDate).getTime() : 0;
      return bDate - aDate;
    });
  }, [selectedMap]);

  const qrUrlFor = (id) => `${window.location.origin}/dvi/scan/${id}`;

  const getSortIndicator = (column) => {
    if (sortColumn !== column) return "";
    return sortDirection === "asc" ? " ▲" : " ▼";
  };

  const handleSort = (column) => {
    setPageNumber(1);
    setSortColumn((prevColumn) => {
      if (prevColumn === column) {
        setSortDirection((prevDirection) =>
          prevDirection === "asc" ? "desc" : "asc"
        );
        return prevColumn;
      }

      setSortDirection("asc");
      return column;
    });
  };

  // ── Seven hidden print areas ──────────────────────────────────────────────
  const qrRef = useRef(null);
  const stickerRef = useRef(null);
  const bothRef = useRef(null);
  const altStickerRef = useRef(null);
  const bothAltRef = useRef(null);
  const detailStickerRef = useRef(null);
  const bothDetailRef = useRef(null);
  const modernStickerRef = useRef(null);
  const bothModernRef = useRef(null);

  const printOpts = (ref, title, css) => ({
    contentRef: ref,
    documentTitle: title,
    removeAfterPrint: true,
    copyStyles: true,
    pageStyle: css,
    onBeforeGetContent: () => new Promise((r) => requestAnimationFrame(r)),
  });

  const handlePrintQR = useReactToPrint(printOpts(qrRef, "QCar - QR Codes", QR_PRINT_CSS));
  const handlePrintSticker = useReactToPrint(printOpts(stickerRef, "QCar - Window Stickers", STICKER_PRINT_CSS));
  const handlePrintBoth = useReactToPrint(printOpts(bothRef, "QCar - QR + Window Stickers", BOTH_PRINT_CSS));
  const handlePrintAltSticker = useReactToPrint(printOpts(altStickerRef, "QCar - Window Stickers (Hero)", ALT_STICKER_PRINT_CSS));
  const handlePrintBothAlt = useReactToPrint(printOpts(bothAltRef, "QCar - QR + Window Stickers (Hero)", BOTH_ALT_PRINT_CSS));
  const handlePrintDetailSticker = useReactToPrint(printOpts(detailStickerRef, "QCar - Window Stickers (Detail)", DETAIL_STICKER_PRINT_CSS));
  const handlePrintBothDetail = useReactToPrint(printOpts(bothDetailRef, "QCar - QR + Window Stickers (Detail)", BOTH_DETAIL_PRINT_CSS));
  const handlePrintModernSticker = useReactToPrint(printOpts(modernStickerRef, "QCar - Window Stickers (Modern)", MODERN_STICKER_PRINT_CSS));
  const handlePrintBothModern = useReactToPrint(printOpts(bothModernRef, "QCar - QR + Window Stickers (Modern)", BOTH_MODERN_PRINT_CSS));

  // ── Template + include-QR selection (persisted to localStorage) ──
  const [templateId, setTemplateId] = useState(() => {
    try {
      const stored = window.localStorage?.getItem(TEMPLATE_STORAGE_KEY);
      if (stored && TEMPLATES.some((t) => t.id === stored)) return stored;
    } catch (_) { /* localStorage may be blocked */ }
    return "classic";
  });
  const [includeQR, setIncludeQR] = useState(() => {
    try {
      return window.localStorage?.getItem(INCLUDE_QR_STORAGE_KEY) === "true";
    } catch (_) {
      return false;
    }
  });

  useEffect(() => {
    try { window.localStorage?.setItem(TEMPLATE_STORAGE_KEY, templateId); } catch (_) { /* noop */ }
  }, [templateId]);
  useEffect(() => {
    try { window.localStorage?.setItem(INCLUDE_QR_STORAGE_KEY, includeQR ? "true" : "false"); } catch (_) { /* noop */ }
  }, [includeQR]);

  // Dispatch the single "Print" action to the right existing handler based on
  // (templateId, includeQR). All hidden print render areas below remain unchanged.
  const printForCurrentSelection = useCallback(() => {
    if (templateId === "qr") return handlePrintQR?.();
    const stickerHandlers = {
      classic: { sticker: handlePrintSticker,        both: handlePrintBoth },
      hero:    { sticker: handlePrintAltSticker,     both: handlePrintBothAlt },
      detail:  { sticker: handlePrintDetailSticker,  both: handlePrintBothDetail },
      modern:  { sticker: handlePrintModernSticker,  both: handlePrintBothModern },
    };
    const pair = stickerHandlers[templateId];
    if (!pair) return;
    (includeQR ? pair.both : pair.sticker)?.();
  }, [
    templateId, includeQR,
    handlePrintQR,
    handlePrintSticker, handlePrintBoth,
    handlePrintAltSticker, handlePrintBothAlt,
    handlePrintDetailSticker, handlePrintBothDetail,
    handlePrintModernSticker, handlePrintBothModern,
  ]);

  const offscreen = { position: "fixed", left: 0, top: 0, width: 0, height: 0, overflow: "hidden", opacity: 0, pointerEvents: "none" };


  // ===== Print CSS =====
  const CRITICAL_PRINT_CSS = `
    @page { size: Letter portrait; margin: 0 }

    html, body {
      margin: 0 !important;
      padding: 0 !important;
      background: #fff !important;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
      font-family: "Segoe UI", Arial, Helvetica, sans-serif !important;
    }
    *, *::before, *::after { box-sizing: border-box; }

    .no-print { display: none !important; }
    .print-page { break-after: avoid; page-break-after: avoid; }

    .preview-stack { display: block !important; }
    .page-shadow { all: unset; display: block; }

    .qr-fullpage {
      width: 100% !important;
      min-height: calc(11in - 1in) !important;
      padding: 0 !important;
      display: flex !important;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      text-align: center;
      color: #000 !important;
    }

    .qr-box { width: 100% !important; display: grid !important; place-items: center; }

    .screen-only { display: none !important; }
    .print-only  { display: block !important; }

    .qr-meta { margin-top: 8pt; font-size: 12pt; line-height: 1.25; color: #000 !important; }
    .qr-title { font-weight: 700; font-size: 13pt; color: #000 !important; }
  `;

  const printAreaRef = useRef(null);
  const handlePrint = useReactToPrint({
    contentRef: printAreaRef,
    documentTitle: "QCar - Inspection QRs",
    removeAfterPrint: true,
    copyStyles: true,
    pageStyle: CRITICAL_PRINT_CSS,
    onBeforeGetContent: () =>
      new Promise((resolve) => requestAnimationFrame(resolve)),
  });

  const [printing, setPrinting] = useState(false);

  const goNext = () => {
    if (!canProceed) return;

    if (isMobile) {
      setPrinting(true);
      requestAnimationFrame(() => {
        try {
          handlePrint?.();
        } finally {
          setTimeout(() => setPrinting(false), 800);
        }
      });
      return;
    }

    setStep(2);
  };

  const pageStart = totalCount === 0 ? 0 : (pageNumber - 1) * pageSize + 1;
  const pageEnd = Math.min(pageNumber * pageSize, totalCount);

  return (
    <Container fluid className="py-3">
      <style>{`
        .screen-only { display: block; }
        .print-only  { display: none; }

        /* ── Full-page QR preview (Step 2) ── */
        .preview-stack { display: grid; gap: 24px; }
        .page-shadow {
          background: white;
          margin: 0 auto;
          width: 8.5in;
          height: 11in;
          max-width: 95vw;
          aspect-ratio: 8.5 / 11;
          box-shadow: 0 6px 24px rgba(0,0,0,0.18);
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          overflow: hidden;
        }
        .qr-fullpage {
          width: 100%;
          height: 100%;
          padding: 0;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          text-align: center;
          color: #000 !important;
        }
        .qr-box   { width: 100%; display: grid; place-items: center; }
        .qr-meta  { margin-top: 12px; font-size: 1rem; line-height: 1.2; word-break: break-word; color: #000 !important; }
        .qr-title { font-weight: 700; font-size: 1.1rem; color: #000 !important; }

        /* Force print layout inside the desktop QR preview (hides screen-only QR widget,
           shows the print-perfect scanme/logo layout instead) */
        .desktop-print-preview .screen-only { display: none !important; }
        .desktop-print-preview .print-only  { display: block !important; }
        /* Anchor content near the top so it doesn't feel "floating" on a tall page */
        .desktop-print-preview .qr-fullpage {
          justify-content: flex-start !important;
          padding-top: 0.75in !important;
        }
      `}</style>

      {/* Hidden: QR-only */}
      <div aria-hidden="true" style={offscreen}>
        <div ref={qrRef}>
          <style media="print">{QR_PRINT_CSS}</style>
          {selectedList.map((item) => (
            <div className="qr-print-wrapper print-page" key={item.id}>
              <QrOnlyPage item={item} />
            </div>
          ))}
        </div>
      </div>

      {/* Hidden: Sticker-only */}
      <div aria-hidden="true" style={offscreen}>
        <div ref={stickerRef}>
          <style media="print">{STICKER_PRINT_CSS}</style>
          {selectedList.map((item) => (
            <div className="print-page" key={item.id}>
              <WindowStickerPage item={item} dealerProfile={dealerProfile} />
            </div>
          ))}
        </div>
      </div>

      {/* Hidden: Both — QR then Classic Sticker per vehicle */}
      <div aria-hidden="true" style={offscreen}>
        <div ref={bothRef}>
          <style media="print">{BOTH_PRINT_CSS}</style>
          {selectedList.map((item) => (
            <React.Fragment key={item.id}>
              <div className="qr-print-wrapper print-page">
                <QrOnlyPage item={item} />
              </div>
              <div className="print-page">
                <WindowStickerPage item={item} dealerProfile={dealerProfile} />
              </div>
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Hidden: Hero Sticker only */}
      <div aria-hidden="true" style={offscreen}>
        <div ref={altStickerRef}>
          <style media="print">{ALT_STICKER_PRINT_CSS}</style>
          {selectedList.map((item) => (
            <div className="print-page" key={item.id}>
              <WindowStickerAltPage item={item} dealerProfile={dealerProfile} />
            </div>
          ))}
        </div>
      </div>

      {/* Hidden: Both — QR then Hero Sticker per vehicle */}
      <div aria-hidden="true" style={offscreen}>
        <div ref={bothAltRef}>
          <style media="print">{BOTH_ALT_PRINT_CSS}</style>
          {selectedList.map((item) => (
            <React.Fragment key={item.id}>
              <div className="qr-print-wrapper print-page">
                <QrOnlyPage item={item} />
              </div>
              <div className="print-page">
                <WindowStickerAltPage item={item} dealerProfile={dealerProfile} />
              </div>
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Hidden: Detail Sticker only */}
      <div aria-hidden="true" style={offscreen}>
        <div ref={detailStickerRef}>
          <style media="print">{DETAIL_STICKER_PRINT_CSS}</style>
          {selectedList.map((item) => (
            <div className="print-page" key={item.id}>
              <WindowStickerDetailPage item={item} dealerProfile={dealerProfile} />
            </div>
          ))}
        </div>
      </div>

      {/* Hidden: Both — QR then Detail Sticker per vehicle */}
      <div aria-hidden="true" style={offscreen}>
        <div ref={bothDetailRef}>
          <style media="print">{BOTH_DETAIL_PRINT_CSS}</style>
          {selectedList.map((item) => (
            <React.Fragment key={item.id}>
              <div className="qr-print-wrapper print-page">
                <QrOnlyPage item={item} />
              </div>
              <div className="print-page">
                <WindowStickerDetailPage item={item} dealerProfile={dealerProfile} />
              </div>
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Hidden: Modern Sticker only */}
      <div aria-hidden="true" style={offscreen}>
        <div ref={modernStickerRef}>
          <style media="print">{MODERN_STICKER_PRINT_CSS}</style>
          {selectedList.map((item) => (
            <div className="print-page" key={item.id}>
              <WindowStickerModernPage item={item} dealerProfile={dealerProfile} />
            </div>
          ))}
        </div>
      </div>

      {/* Hidden: Both — QR then Modern Sticker per vehicle */}
      <div aria-hidden="true" style={offscreen}>
        <div ref={bothModernRef}>
          <style media="print">{BOTH_MODERN_PRINT_CSS}</style>
          {selectedList.map((item) => (
            <React.Fragment key={item.id}>
              <div className="qr-print-wrapper print-page">
                <QrOnlyPage item={item} />
              </div>
              <div className="print-page">
                <WindowStickerModernPage item={item} dealerProfile={dealerProfile} />
              </div>
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* ════════════════════ STEP 1 — Selection ════════════════════ */}
      {step === 1 && (
        <>
          <Row className="mb-3">
            <Col><h3 className="mb-0">Print Documents — Select Inspections</h3></Col>
          </Row>

          <Card className="mb-3">
            <Card.Body>
              <Row className="g-2 align-items-end">
                <Col md={4}>
                  <Form.Label className="mb-1">Search</Form.Label>
                  <InputGroup>
                    <Form.Control
                      placeholder="VIN, Make Model, Stock #..."
                      value={searchInput}
                      onChange={(e) => {
                        const value = e.target.value;
                        setSearchInput(value);
                        debouncedSearch(value);
                      }}
                    />
                    {search && (
                      <Button variant="outline-secondary" onClick={() => setSearch("")}>Clear</Button>
                    )}
                  </InputGroup>
                </Col>
                <Col md="auto" className="ms-auto">
                  <div className="text-end">
                    <div className="small text-muted">Selected: {selectedIds.size}</div>
                    <Button variant="outline-secondary" size="sm" className="mt-2" onClick={clearAll} disabled={selectedIds.size === 0}>
                      Clear Selected
                    </Button>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <div>Inspections</div>
              <Button variant="outline-primary" size="sm" onClick={toggleAllOnPage} disabled={inspections.length === 0}>
                {inspections.length > 0 && inspections.every((x) => selectedIds.has(x.id)) ? "Unselect All" : "Select All"}
              </Button>
            </Card.Header>

            <Card.Body className="p-0">
              {loading && <div className="p-3 text-muted">Loading inspections…</div>}
              {error && <div className="p-3 text-danger">{error}</div>}
              {!loading && !error && inspections.length === 0 && <div className="p-3 text-muted">No inspections found.</div>}

              {!loading && !error && inspections.length > 0 && (
                <div className="table-responsive">
                  <Table className="mb-0 align-middle">
                    <thead className="table-light">
                      <tr>
                        <th style={{ width: 48 }} />
                        <th>Year</th><th>Make</th><th>Model</th>
                        <th>Stock #</th><th>VIN</th><th>Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {inspections.map((row) => (
                        <tr key={row.id} onClick={() => toggleOne(row)} style={{ cursor: "pointer" }}>
                          {/* Stop click propagation in this cell so clicking
                              the checkbox doesn't double-fire toggleOne (once
                              via onChange, again via the <tr> onClick). */}
                          <td onClick={(e) => e.stopPropagation()}>
                            <Form.Check
                              type="checkbox"
                              checked={selectedIds.has(row.id)}
                              onChange={() => toggleOne(row)}
                            />
                          </td>
                          <td>{row.vehicleYear || "—"}</td>
                          <td>{row.vehicleMake || "—"}</td>
                          <td>{row.vehicleModel || "—"}</td>
                          <td>{row.vehicleStockNumber || "—"}</td>
                          <td>{row.vehicleVin || "—"}</td>
                          <td>{row.inspectionDate ? row.inspectionDate.toLocaleString() : "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </Card.Body>

            <Card.Footer className="d-flex justify-content-end no-print">
              <Button variant="primary" disabled={!canProceed} onClick={() => setStep(2)}>
                Next: Full-Page Previews →
              </Button>
            </Card.Footer>
          </Card>
        </>
      )}

      {/* ════════════════════ STEP 2 — Preview + Print ════════════════════ */}
      {step === 2 && (
        <>
          <PreviewToolbar
            count={selectedList.length}
            templateId={templateId}
            onTemplateChange={setTemplateId}
            includeQR={includeQR}
            onIncludeQRChange={setIncludeQR}
            onBack={() => setStep(1)}
            onPrint={printForCurrentSelection}
          />

          <SingleTemplatePreview
            selectedList={selectedList}
            dealerProfile={dealerProfile}
            templateId={templateId}
          />
        </>
      )}
    </Container>
  );
};

export default QrBatchPrint;